import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import HelpLayout from '../../../components/help/HelpLayout';
import { 
  BriefcaseIcon,
  PlusIcon,
  PencilIcon,
  DocumentTextIcon,
  ChartBarIcon,
  UserGroupIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';

const OpportunityManagementHelp = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const breadcrumbs = [
    { name: 'Admin Guide', href: '/help/admin/dashboard' },
    { name: 'Opportunity Management' }
  ];

  const managementTasks = [
    {
      title: 'Create Opportunities',
      description: 'Add new job opportunities, apprenticeships, and internships',
      icon: PlusIcon,
      features: [
        'Job posting creation',
        'Requirement specification',
        'Application deadline setting',
        'Capacity management'
      ]
    },
    {
      title: 'Edit & Update',
      description: 'Modify existing opportunities and keep information current',
      icon: PencilIcon,
      features: [
        'Content updates',
        'Deadline extensions',
        'Requirement changes',
        'Status modifications'
      ]
    },
    {
      title: 'Application Review',
      description: 'Process and manage incoming applications',
      icon: DocumentTextIcon,
      features: [
        'Application screening',
        'Candidate evaluation',
        'Status updates',
        'Communication tools'
      ]
    },
    {
      title: 'Analytics & Reporting',
      description: 'Track performance and generate insights',
      icon: ChartBarIcon,
      features: [
        'Application metrics',
        'Success rates',
        'Employer feedback',
        'Trend analysis'
      ]
    }
  ];

  const opportunityStatuses = [
    { status: 'Draft', description: 'Opportunity is being prepared but not yet published', color: '#6B7280' },
    { status: 'Active', description: 'Published and accepting applications', color: '#10B981' },
    { status: 'Closed', description: 'No longer accepting applications', color: '#F59E0B' },
    { status: 'Filled', description: 'Position has been filled', color: '#8B5CF6' },
    { status: 'Cancelled', description: 'Opportunity has been cancelled', color: '#EF4444' }
  ];

  const applicationStatuses = [
    { status: 'Pending', description: 'Application received, awaiting review', color: '#6B7280' },
    { status: 'Under Review', description: 'Application is being evaluated', color: '#F59E0B' },
    { status: 'Shortlisted', description: 'Candidate has been shortlisted', color: '#3B82F6' },
    { status: 'Accepted', description: 'Application has been accepted', color: '#10B981' },
    { status: 'Rejected', description: 'Application has been declined', color: '#EF4444' }
  ];

  return (
    <HelpLayout title="Opportunity Management" breadcrumbs={breadcrumbs}>
      <div className="help-content">
        {/* Introduction */}
        <div className="card" style={{ marginBottom: '32px' }}>
          <div className="card-body">
            <h2 style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: 'var(--primary-black)',
              marginBottom: '16px'
            }}>
              Opportunity Management Guide
            </h2>
            <p style={{
              fontSize: '16px',
              color: 'var(--text-gray)',
              lineHeight: 1.6,
              marginBottom: '24px'
            }}>
              The Opportunity Management system allows administrators to create, manage, and track 
              job opportunities, apprenticeships, and internships. This comprehensive guide covers 
              all aspects of opportunity administration from creation to completion.
            </p>

            {/* Quick Access */}
            <div style={{
              padding: '20px',
              backgroundColor: 'var(--primary-yellow-light)',
              borderRadius: '8px',
              border: '1px solid var(--primary-yellow)'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: 'bold',
                color: 'var(--primary-black)',
                marginBottom: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                🚀 Quick Access
              </h3>
              <p style={{ color: 'var(--text-gray)', margin: 0, lineHeight: 1.6 }}>
                Access opportunity management through the <strong>Admin Dashboard</strong> or navigate 
                directly to <strong>Opportunities → Create</strong> to add new opportunities.
              </p>
            </div>
          </div>
        </div>

        {/* Management Tasks */}
        <div className="card" style={{ marginBottom: '32px' }}>
          <div className="card-body">
            <h3 style={{
              fontSize: '20px',
              fontWeight: 'bold',
              color: 'var(--primary-black)',
              marginBottom: '24px'
            }}>
              💼 Core Management Tasks
            </h3>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '24px'
            }}>
              {managementTasks.map((task, index) => {
                const Icon = task.icon;
                return (
                  <div key={index} style={{
                    padding: '24px',
                    border: '1px solid var(--border-gray)',
                    borderRadius: '8px',
                    backgroundColor: 'var(--white)'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      marginBottom: '16px'
                    }}>
                      <Icon style={{
                        width: '24px',
                        height: '24px',
                        color: 'var(--primary-yellow)'
                      }} />
                      <h4 style={{
                        fontSize: '16px',
                        fontWeight: 'bold',
                        color: 'var(--primary-black)',
                        margin: 0
                      }}>
                        {task.title}
                      </h4>
                    </div>
                    <p style={{
                      fontSize: '14px',
                      color: 'var(--text-gray)',
                      lineHeight: 1.6,
                      marginBottom: '16px'
                    }}>
                      {task.description}
                    </p>
                    <ul style={{
                      fontSize: '14px',
                      color: 'var(--text-gray)',
                      lineHeight: 1.6,
                      paddingLeft: '16px',
                      margin: 0
                    }}>
                      {task.features.map((feature, idx) => (
                        <li key={idx}>{feature}</li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Creating Opportunities */}
        <div className="card" style={{ marginBottom: '32px' }}>
          <div className="card-body">
            <h3 style={{
              fontSize: '20px',
              fontWeight: 'bold',
              color: 'var(--primary-black)',
              marginBottom: '24px'
            }}>
              ➕ Creating New Opportunities
            </h3>

            <div style={{ marginBottom: '24px' }}>
              <h4 style={{
                fontSize: '16px',
                fontWeight: '600',
                color: 'var(--primary-black)',
                marginBottom: '16px'
              }}>
                Step-by-Step Creation Process
              </h4>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {[
                  {
                    step: '1',
                    title: 'Basic Information',
                    description: 'Enter opportunity title, type (job/apprenticeship/internship), and company details.'
                  },
                  {
                    step: '2',
                    title: 'Description & Requirements',
                    description: 'Provide detailed job description, qualifications, and application requirements.'
                  },
                  {
                    step: '3',
                    title: 'Timeline & Capacity',
                    description: 'Set application deadlines, start dates, and maximum number of positions.'
                  },
                  {
                    step: '4',
                    title: 'Location & Contact',
                    description: 'Specify work location and provide contact information for applicants.'
                  },
                  {
                    step: '5',
                    title: 'Review & Publish',
                    description: 'Review all information and publish the opportunity or save as draft.'
                  }
                ].map((item, index) => (
                  <div key={index} style={{
                    display: 'flex',
                    gap: '16px',
                    padding: '16px',
                    backgroundColor: 'var(--background-gray)',
                    borderRadius: '8px'
                  }}>
                    <div style={{
                      width: '32px',
                      height: '32px',
                      backgroundColor: 'var(--primary-yellow)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      color: 'var(--white)',
                      flexShrink: 0
                    }}>
                      {item.step}
                    </div>
                    <div>
                      <h5 style={{
                        fontSize: '16px',
                        fontWeight: 'bold',
                        color: 'var(--primary-black)',
                        marginBottom: '4px'
                      }}>
                        {item.title}
                      </h5>
                      <p style={{
                        fontSize: '14px',
                        color: 'var(--text-gray)',
                        lineHeight: 1.5,
                        margin: 0
                      }}>
                        {item.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div style={{
              padding: '16px',
              backgroundColor: 'var(--background-gray)',
              borderRadius: '6px',
              borderLeft: '4px solid var(--primary-yellow)'
            }}>
              <h5 style={{
                fontSize: '14px',
                fontWeight: 'bold',
                color: 'var(--primary-black)',
                marginBottom: '8px'
              }}>
                💡 Best Practices
              </h5>
              <ul style={{
                fontSize: '14px',
                color: 'var(--text-gray)',
                lineHeight: 1.5,
                paddingLeft: '16px',
                margin: 0
              }}>
                <li>Use clear, descriptive titles that accurately reflect the position</li>
                <li>Provide comprehensive job descriptions with specific responsibilities</li>
                <li>List all required qualifications and preferred skills</li>
                <li>Set realistic application deadlines with adequate processing time</li>
                <li>Include contact information for applicant inquiries</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Status Management */}
        <div className="card" style={{ marginBottom: '32px' }}>
          <div className="card-body">
            <h3 style={{
              fontSize: '20px',
              fontWeight: 'bold',
              color: 'var(--primary-black)',
              marginBottom: '24px'
            }}>
              📊 Opportunity Status Management
            </h3>

            <div style={{ marginBottom: '24px' }}>
              <h4 style={{
                fontSize: '16px',
                fontWeight: '600',
                color: 'var(--primary-black)',
                marginBottom: '16px'
              }}>
                Opportunity Statuses
              </h4>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '12px'
              }}>
                {opportunityStatuses.map((item, index) => (
                  <div key={index} style={{
                    padding: '12px',
                    backgroundColor: 'var(--background-gray)',
                    borderRadius: '6px',
                    borderLeft: `4px solid ${item.color}`
                  }}>
                    <div style={{
                      fontSize: '14px',
                      fontWeight: 'bold',
                      color: item.color,
                      marginBottom: '4px'
                    }}>
                      {item.status}
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: 'var(--text-gray)',
                      lineHeight: 1.4
                    }}>
                      {item.description}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h4 style={{
                fontSize: '16px',
                fontWeight: '600',
                color: 'var(--primary-black)',
                marginBottom: '16px'
              }}>
                Application Statuses
              </h4>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '12px'
              }}>
                {applicationStatuses.map((item, index) => (
                  <div key={index} style={{
                    padding: '12px',
                    backgroundColor: 'var(--background-gray)',
                    borderRadius: '6px',
                    borderLeft: `4px solid ${item.color}`
                  }}>
                    <div style={{
                      fontSize: '14px',
                      fontWeight: 'bold',
                      color: item.color,
                      marginBottom: '4px'
                    }}>
                      {item.status}
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: 'var(--text-gray)',
                      lineHeight: 1.4
                    }}>
                      {item.description}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Application Management */}
        <div className="card" style={{ marginBottom: '32px' }}>
          <div className="card-body">
            <h3 style={{
              fontSize: '20px',
              fontWeight: 'bold',
              color: 'var(--primary-black)',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <UserGroupIcon style={{ width: '24px', height: '24px', color: 'var(--primary-yellow)' }} />
              Application Management
            </h3>
            <p style={{
              fontSize: '16px',
              color: 'var(--text-gray)',
              lineHeight: 1.6,
              marginBottom: '16px'
            }}>
              Efficiently manage incoming applications with these key features:
            </p>
            <ul style={{
              color: 'var(--text-gray)',
              lineHeight: 1.6,
              paddingLeft: '20px',
              marginBottom: '16px'
            }}>
              <li><strong>Bulk Actions:</strong> Process multiple applications simultaneously</li>
              <li><strong>Filtering:</strong> Sort applications by status, date, or qualifications</li>
              <li><strong>Communication:</strong> Send updates and notifications to applicants</li>
              <li><strong>Export:</strong> Download application data for external processing</li>
              <li><strong>Notes:</strong> Add internal notes and evaluation comments</li>
            </ul>
          </div>
        </div>

        {/* Analytics & Reporting */}
        <div className="card" style={{ marginBottom: '32px' }}>
          <div className="card-body">
            <h3 style={{
              fontSize: '20px',
              fontWeight: 'bold',
              color: 'var(--primary-black)',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <ChartBarIcon style={{ width: '24px', height: '24px', color: 'var(--primary-yellow)' }} />
              Analytics & Reporting
            </h3>
            <p style={{
              fontSize: '16px',
              color: 'var(--text-gray)',
              lineHeight: 1.6,
              marginBottom: '16px'
            }}>
              Track opportunity performance and generate insights with comprehensive analytics:
            </p>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '16px'
            }}>
              {[
                { title: 'Application Metrics', description: 'Track application volumes, conversion rates, and trends' },
                { title: 'Success Rates', description: 'Monitor placement success and completion rates' },
                { title: 'Employer Feedback', description: 'Collect and analyze employer satisfaction data' },
                { title: 'Performance Reports', description: 'Generate detailed reports for stakeholders' }
              ].map((metric, index) => (
                <div key={index} style={{
                  padding: '16px',
                  backgroundColor: 'var(--background-gray)',
                  borderRadius: '6px'
                }}>
                  <h5 style={{
                    fontSize: '14px',
                    fontWeight: 'bold',
                    color: 'var(--primary-black)',
                    marginBottom: '8px'
                  }}>
                    {metric.title}
                  </h5>
                  <p style={{
                    fontSize: '14px',
                    color: 'var(--text-gray)',
                    lineHeight: 1.5,
                    margin: 0
                  }}>
                    {metric.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Next Steps */}
        <div className="card">
          <div className="card-body">
            <h3 style={{
              fontSize: '20px',
              fontWeight: 'bold',
              color: 'var(--primary-black)',
              marginBottom: '16px'
            }}>
              🎯 Next Steps
            </h3>
            <div style={{
              display: 'flex',
              gap: '16px',
              flexWrap: 'wrap'
            }}>
              <Link to="/opportunities/create" className="btn btn-primary">
                Create Opportunity
              </Link>
              <Link to="/opportunities/applications" className="btn btn-outline">
                Manage Applications
              </Link>
              <Link to="/help/admin/dashboard" className="btn btn-outline">
                Admin Dashboard
              </Link>
            </div>
          </div>
        </div>
      </div>
    </HelpLayout>
  );
};

export default OpportunityManagementHelp;
